/**
 * Onboarding shell: loads the user's progress, opens the welcome on first login, offers each page's tour
 * the first time the user visits it, and exposes the Help-menu actions. Mount once inside the router and
 * the session (see the registration lines in the onboarding report); place <OnboardingHelpItems/> in the
 * top bar's Help menu.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Compass, PartyPopper, Route as RouteIcon } from 'lucide-react';
import type { OnboardingState, TourKey } from '@bs/contracts';
import { useSession } from '../lib/session.js';
import { notifyError } from '../lib/toast.js';
import { Titled } from '../modules/review/HelpTip.js';
import { Sheet } from '../components/ui/sheet.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';
import { ErrorState } from '../components/ui/states.js';
import { DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu.js';
import { onbCall, ONB_KEY } from './onboardingApi.js';
import { TOURS, findTarget, tourFor, tourKeyForPath, type Tour } from './tours.js';
import { runTour, type TourEnd } from './runTour.js';
import { WelcomeWizard } from './WelcomeWizard.js';


interface OnbCtx {
  state: OnboardingState | null;
  startTour: (key: TourKey) => void;
  /** Tour of the page the user is on, or null when this page has none. */
  currentTour: TourKey | null;
  showWelcome: () => void;
  showAllTours: () => void;
  /** Record "Not now" for a tour without running it. */
  markSkipped: (key: TourKey) => void;
  /** The tour on screen right now, or null. */
  activeTour: TourKey | null;
}
const Ctx = createContext<OnbCtx | null>(null);
/** The onboarding context, or null outside the provider (panels rendered alone in tests). */
export function useOnboardingOptional(): OnbCtx | null { return useContext(Ctx); }
export function useOnboarding(): OnbCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('OnboardingProvider missing');
  return c;
}

/** Calls `dismiss` once a dialog or side panel appears; returns the cleanup. Exported for tests. */
export function dismissWhenDialogOpens(dismiss: () => void, root: HTMLElement = document.body): () => void {
  const hasDialog = () => !!root.querySelector('[role="dialog"], [role="alertdialog"]');
  if (hasDialog()) { dismiss(); return () => undefined; }
  const obs = new MutationObserver(() => { if (hasDialog()) { dismiss(); obs.disconnect(); } });
  obs.observe(root, { childList: true, subtree: true });
  const stop = window.setTimeout(() => obs.disconnect(), 13_000);
  return () => { obs.disconnect(); window.clearTimeout(stop); };
}

/**
 * Wait for the page before pointing at things: its data has loaded (no loading skeleton left) and, for a tour
 * that needs something opened first (the person panel), open it. Gives up after ~6 s and runs what is there.
 */
export function whenPageReady(tour: Tour, fn: () => void): void {
  const started = Date.now();
  let opened = false;
  const firstTarget = tour.steps.find((s) => s.target)?.target;
  const tick = () => {
    const late = Date.now() - started > 6000;
    if (tour.opener && !opened && !findTarget(firstTarget)) {
      const el = document.querySelector<HTMLElement>(tour.opener);
      if (el) { opened = true; el.click(); }
    }
    const busy = !!document.querySelector('main .animate-shimmer, [role="dialog"] .animate-shimmer');
    if (late || (!busy && (!firstTarget || findTarget(firstTarget)))) { window.setTimeout(fn, 250); return; }
    window.setTimeout(tick, 150);
  };
  window.setTimeout(tick, 150);
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [toursOpen, setToursOpen] = useState(false);
  const autoOpened = useRef(false);
  const offered = useRef(new Set<string>());
  const running = useRef(false);
  const [activeTour, setActiveTour] = useState<TourKey | null>(null);

  const q = useQuery({ queryKey: ONB_KEY, queryFn: () => onbCall('getOnboarding'), enabled: !!me, staleTime: 5 * 60_000 });
  const state = q.data ?? null;

  const mark = useMutation({
    mutationFn: (v: { key: TourKey; status: TourEnd }) => onbCall('markTour', { params: { tourKey: v.key }, body: { status: v.status } }),
    onSuccess: (s) => qc.setQueryData(ONB_KEY, s),
    onError: notifyError,
  });
  const restart = useMutation({
    mutationFn: () => onbCall('restartOnboarding'),
    onSuccess: (s) => { qc.setQueryData(ONB_KEY, s); setWizardOpen(true); },
    onError: notifyError,
  });
  const reset = useMutation({
    mutationFn: () => onbCall('resetTours'),
    onSuccess: (s) => qc.setQueryData(ONB_KEY, s),
    onError: notifyError,
  });

  // First login: open the welcome once per page load while it is not finished or skipped.
  useEffect(() => {
    if (state?.showWizard && !autoOpened.current) { autoOpened.current = true; setWizardOpen(true); }
  }, [state?.showWizard]);

  const startTour = useCallback((key: TourKey) => {
    if (running.current) return;
    const tour = tourFor(key);
    offered.current.add(key);
    // Go to the tour's page (and setup tab) unless already there; the person panel runs on any open person.
    const here = tourKeyForPath(pathname, search);
    if (here !== key && !(key === 'person' && here === 'person')) {
      if (pathname !== tour.path || (tour.search && tour.search !== search)) navigate(`${tour.path}${tour.search ?? ''}`);
    }
    running.current = true;
    setActiveTour(key);
    whenPageReady(tour, () => runTour(tour, (how) => { running.current = false; setActiveTour(null); mark.mutate({ key, status: how }); }));
  }, [mark, navigate, pathname, search]);

  // First visit to a page with a tour: offer it once (never while the welcome is open).
  const currentTour = tourKeyForPath(pathname, search);
  useEffect(() => {
    // The person panel offers its own tour inside the panel (PersonTourOffer): a toast would sit behind it.
    if (!state || state.showWizard || wizardOpen || !currentTour || currentTour === 'person' || running.current) return;
    if (state.toursSeen[currentTour] || offered.current.has(currentTour)) return;
    offered.current.add(currentTour);
    const t = tourFor(currentTour);
    const id = toast(`New here? Take a one-minute tour of ${t.title}.`, {
      duration: 12_000,
      action: { label: 'Show me', onClick: () => startTour(currentTour) },
      cancel: { label: 'Not now', onClick: () => mark.mutate({ key: currentTour, status: 'skipped' }) },
    });
    // The offer sits bottom-right, where side panels keep their main buttons (Save, "Change anyway").
    // Take it away as soon as any panel or dialog opens, so it never covers them (BS-070). Not tied to
    // this effect's cleanup (its deps change on every render); the watcher stops itself after 13 s.
    dismissWhenDialogOpens(() => toast.dismiss(id));
  }, [state, wizardOpen, currentTour, startTour, mark]);

  const value: OnbCtx = {
    state, startTour, currentTour,
    showWelcome: () => restart.mutate(),
    showAllTours: () => setToursOpen(true),
    markSkipped: (key) => mark.mutate({ key, status: 'skipped' }),
    activeTour,
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {state && me && (
        <WelcomeWizard state={state} meId={me.id} open={wizardOpen} onOpenChange={setWizardOpen} onStartTour={() => startTour('welcome-next')} />
      )}
      <AllToursSheet open={toursOpen} onOpenChange={setToursOpen} state={state} error={q.error} onRetry={() => void q.refetch()}
        onStart={(k) => { setToursOpen(false); startTour(k); }} onReset={() => reset.mutate()} resetting={reset.isPending} />
    </Ctx.Provider>
  );
}

export function AllToursSheet({ open, onOpenChange, state, error, onRetry, onStart, onReset, resetting }: {
  open: boolean; onOpenChange: (o: boolean) => void; state: OnboardingState | null; error: unknown; onRetry: () => void;
  onStart: (k: TourKey) => void; onReset: () => void; resetting: boolean;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={<Titled k="onb.tours">All tours</Titled>} description="A short walk through each page. Take any of them again at any time."
      footer={<Button variant="outline" loading={resetting} onClick={onReset}>Offer every tour again</Button>}>
      {error && !state ? <ErrorState error={error} onRetry={onRetry} /> : (
        <ul className="divide-y">
          {TOURS.map((t) => {
            const seen = state?.toursSeen[t.key];
            return (
              <li key={t.key} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium">{t.title}</p>
                  <p className="text-sm text-muted-foreground">{t.summary} {t.steps.length} steps.</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={seen?.status === 'done' ? 'success' : 'neutral'}>{seen?.status === 'done' ? 'Taken' : seen ? 'Skipped' : 'Not taken'}</Badge>
                  <Button size="sm" variant="outline" onClick={() => onStart(t.key)}>Take tour</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}

/** Items for the top bar's Help menu. */
export function OnboardingHelpItems() {
  const o = useOnboarding();
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled={!o.currentTour} onSelect={() => { if (o.currentTour) o.startTour(o.currentTour); }}>
        <RouteIcon /> Take the tour of this page
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => o.showWelcome()}><PartyPopper /> Show the welcome again</DropdownMenuItem>
      <DropdownMenuItem onSelect={() => o.showAllTours()}><Compass /> All tours</DropdownMenuItem>
    </>
  );
}
